import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Pencil, Loader2, Calendar, Settings2, Trash2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import api from '@/lib/api';
import { StageStatusBadge, formatDatePt } from '@/components/Badges';
import { StageFormDialog } from './components/StageFormDialog';

const EMPTY = {
  title: '', type: 'eliminatoria', date: '', start_time: '21:00', voting_planned_time: '23:00',
  bands_count: 2,
  band_a_id: '', band_b_id: '', band_c_id: '',
  night_password: '', require_night_password: false,
  public_notes: '', internal_notes: '',
};

function StageRow({ stage, onEdit, onDelete }) {
  const day = new Date(stage.date + 'T12:00:00').getDate().toString().padStart(2, '0');
  const month = new Date(stage.date + 'T12:00:00').toLocaleDateString('pt-BR', { month: 'short' }).replace('.', '');
  return (
    <div className="p-4 flex items-center gap-3 flex-wrap" data-testid={`admin-stage-row-${stage.slug}`}>
      <div className="text-center min-w-[64px]">
        <div className="font-display text-2xl text-white">{day}</div>
        <div className="text-[10px] text-muted-foreground uppercase">{month}</div>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="font-semibold text-white">{stage.title}</div>
          <StageStatusBadge status={stage.status} />
        </div>
        <div className="text-xs text-muted-foreground">
          {stage.band_a?.name || 'A definir'} <span className="text-[var(--g66-red)]">vs</span> {stage.band_b?.name || 'A definir'} • {stage.type} • {formatDatePt(stage.date)}
        </div>
      </div>
      <div className="flex gap-2">
        <Button size="sm" variant="secondary" onClick={() => onEdit(stage)}>
          <Pencil className="w-4 h-4" />
        </Button>
        <Link to={`/admin/etapas/${stage.id}`}>
          <Button size="sm" className="bg-[var(--g66-amber)] text-black hover:bg-[var(--g66-amber-2)]" data-testid={`stage-manage-${stage.slug}`}>
            <Settings2 className="w-4 h-4 mr-1" /> Gerenciar
          </Button>
        </Link>
        {stage.status !== 'voting_open' && (
          <Button
            size="sm"
            variant="secondary"
            onClick={() => onDelete(stage)}
            data-testid={`stage-delete-${stage.slug}`}
            className="text-[var(--g66-red-2)]"
            title="Excluir etapa"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        )}
      </div>
    </div>
  );
}

function StagesEmpty() {
  return (
    <Card>
      <CardContent className="p-10 text-center text-muted-foreground">
        <Calendar className="w-8 h-8 mx-auto mb-2" />
        Nenhuma etapa cadastrada.
      </CardContent>
    </Card>
  );
}

function DeleteStageDialog({ target, deleting, onCancel, onConfirm }) {
  return (
    <AlertDialog open={!!target} onOpenChange={(o) => !o && onCancel()}>
      <AlertDialogContent className="bg-card border-border">
        <AlertDialogHeader>
          <AlertDialogTitle>Excluir etapa?</AlertDialogTitle>
          <AlertDialogDescription>
            {target?.status === 'finished' ? (
              <>
                A etapa <strong className="text-white">{target?.title}</strong> está finalizada. Ao excluir, seu resultado, comandas e votos serão removidos <strong>permanentemente</strong> e ela deixará de aparecer no site público. Esta ação NÃO pode ser desfeita.
              </>
            ) : (
              <>
                A etapa <strong className="text-white">{target?.title}</strong> será removida junto com todas as suas comandas e votos. Esta ação NÃO pode ser desfeita.
              </>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={deleting}>Cancelar</AlertDialogCancel>
          <AlertDialogAction
            data-testid="confirm-delete-stage-button"
            onClick={onConfirm}
            disabled={deleting}
            className="bg-[var(--g66-red)] text-white hover:bg-[var(--g66-red-2)]"
          >
            {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Excluir definitivamente'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function toSavePayload(form) {
  const payload = { ...form };
  payload.bands_count = form.bands_count === 3 ? 3 : 2;
  if (payload.band_a_id === '') payload.band_a_id = null;
  if (payload.band_b_id === '') payload.band_b_id = null;
  if (payload.bands_count === 3) {
    if (payload.band_c_id === '') payload.band_c_id = null;
  } else {
    payload.band_c_id = null;
  }
  return payload;
}

function stageToForm(s) {
  return {
    title: s.title, type: s.type, date: s.date, start_time: s.start_time || '21:00',
    voting_planned_time: s.voting_planned_time || '23:00',
    bands_count: s.bands_count || 2,
    band_a_id: s.band_a_id || '', band_b_id: s.band_b_id || '', band_c_id: s.band_c_id || '',
    night_password: s.night_password || '', require_night_password: !!s.require_night_password,
    public_notes: s.public_notes || '', internal_notes: s.internal_notes || '',
  };
}

export default function AdminStages() {
  const [stages, setStages] = useState([]);
  const [bands, setBands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const [s, b] = await Promise.all([api.get('/admin/stages'), api.get('/admin/bands')]);
    setStages(s.data);
    setBands(b.data.filter((x) => x.status !== 'inativa'));
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm(stageToForm(s)); setOpen(true); };

  const save = async () => {
    if (!form.title.trim() || !form.date) { toast.error('Título e data obrigatórios'); return; }
    setSaving(true);
    try {
      const payload = toSavePayload(form);
      if (editing) {
        await api.put(`/admin/stages/${editing.id}`, payload);
        toast.success('Etapa atualizada');
      } else {
        await api.post('/admin/stages', payload);
        toast.success('Etapa criada');
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const deleteStage = async () => {
    if (!confirmDel) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/stages/${confirmDel.id}`);
      toast.success('Etapa excluída');
      setConfirmDel(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao excluir');
    } finally {
      setDeleting(false);
    }
  };

  const renderStagesList = () => {
    if (loading) return <div className="text-muted-foreground">Carregando…</div>;
    if (stages.length === 0) return <StagesEmpty />;
    return (
      <Card className="border border-border bg-card">
        <CardContent className="p-0">
          <div className="divide-y divide-border">
            {stages.map((s) => (
              <StageRow key={s.id} stage={s} onEdit={openEdit} onDelete={setConfirmDel} />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="font-display text-4xl text-white">ETAPAS / DISPUTAS</div>
          <p className="text-muted-foreground">Gerencie as etapas do campeonato.</p>
        </div>
        <Button data-testid="admin-stages-new-button" onClick={openNew} className="bg-[var(--g66-amber)] text-black hover:bg-[var(--g66-amber-2)]">
          <Plus className="w-4 h-4 mr-1" /> Nova etapa
        </Button>
      </div>

      {renderStagesList()}

      <StageFormDialog
        open={open}
        editing={editing}
        form={form}
        setForm={setForm}
        bands={bands}
        saving={saving}
        onSave={save}
        onOpenChange={setOpen}
      />

      <DeleteStageDialog
        target={confirmDel}
        deleting={deleting}
        onCancel={() => setConfirmDel(null)}
        onConfirm={deleteStage}
      />
    </div>
  );
}
